/**
 * Prepares a backend column configuration for a DevExtreme DataGrid.
 *
 * DevExtreme normalizes every `column.lookup` it is given into its own object, the one carrying
 * `calculateCellValue`, `updateValueMap` and `update`. devextreme-react compares the column config by
 * reference and, while the column count is unchanged, patches single options instead of replacing the
 * whole `columns` option, so a new lookup object arrives as `option('columns[i].columns[j].lookup')`.
 * DevExtreme's columnOptionCore then overwrites the normalized lookup with the raw one, and the grid
 * runs on the raw object until fireColumnsChanged reinitializes the columns. Anything that touches a
 * lookup in that window - a refresh, a reload, updateColumnDataTypes - dies with
 * `TypeError: lookup.calculateCellValue is not a function`.
 *
 * Two properties close that window:
 *
 * 1. One object per distinct lookup content, shared across cache keys and across grids. An unchanged
 *    list never changes identity, so it never triggers the patch, whichever location or config key it
 *    arrives under.
 *
 * 2. A `calculateCellValue` of our own on every lookup, so that the patch is survivable when the
 *    content genuinely did change and a new object is unavoidable. It is defined non-enumerable on
 *    purpose: DevExtreme merges the raw lookup into the normalized one with `deepExtendArraySafe`,
 *    which walks enumerable keys, so a non-enumerable member cannot displace DevExtreme's own
 *    implementation on the normal path. It is reached only while the raw object is installed, and
 *    DevExtreme replaces it on the next reinit. `lookup.update` is optional to DevExtreme (it checks
 *    for it), so it does not need a stand-in.
 */

const canonicalByContent = new Map();

// Every own key, so that a field added to the backend's Lookup cannot make two different lists share
// one instance. The cache lives for the session: it holds one object per distinct list the user's
// locations offer, which is the price of a stable identity.
const signatureOf = (lookup) =>
    JSON.stringify(
        Object.keys(lookup)
            .sort()
            .map((key) => [key, lookup[key]])
    );

/**
 * Same resolution the grid cell does: value to label, falling back to the stored value when the list
 * does not contain it. Not a reimplementation of DevExtreme's valueMap, which needs internal state we
 * do not own, but it honours the same `deserializeValue` contract, because updateSerializers attaches
 * one to a lookup whose labels turn out to be dates or numbers - onto this very object once it has
 * been installed on a column.
 */
function calculateCellValue(value, skipDeserialization) {
    const { valueExpr, displayExpr, dataSource, deserializeValue } = this;
    let resolved = value;
    if (displayExpr && Array.isArray(dataSource)) {
        const match = dataSource.find((item) => (valueExpr ? item?.[valueExpr] : item) === value);
        if (match) resolved = match[displayExpr];
    }
    return deserializeValue && !skipDeserialization ? deserializeValue.call(this, resolved) : resolved;
}

const canonicalize = (lookup) => {
    const signature = signatureOf(lookup);
    const cached = canonicalByContent.get(signature);
    if (cached) return cached;

    const canonical = { ...lookup };
    Object.defineProperty(canonical, 'calculateCellValue', { value: calculateCellValue, enumerable: false });
    canonicalByContent.set(signature, canonical);
    return canonical;
};

const withCanonicalLookup = (column) => {
    if (!column || typeof column !== 'object') return column;

    if (Array.isArray(column.columns)) {
        const columns = column.columns.map(withCanonicalLookup);
        return columns.some((c, i) => c !== column.columns[i]) ? { ...column, columns } : column;
    }

    if (!column.lookup || typeof column.lookup !== 'object') return column;
    const lookup = canonicalize(column.lookup);
    return lookup === column.lookup ? column : { ...column, lookup };
};

/**
 * Returns the configuration with every `lookup` replaced by the shared instance for its content.
 * Anything that is not a column tree is handed back untouched.
 */
export const withCanonicalLookups = (columnsConfig) => {
    if (!Array.isArray(columnsConfig)) return columnsConfig;
    const next = columnsConfig.map(withCanonicalLookup);
    return next.some((c, i) => c !== columnsConfig[i]) ? next : columnsConfig;
};
